import { useCallback, useEffect, useRef, useState } from 'react'

import type { QuantRunLogState } from '@/app/hooks/useQuantRunLog'
import { nextDelay } from '@/app/lib/quant/backoff'
import { quantApi } from '@/app/lib/quant/quantApi'
import { quantErrorMessage } from '@/app/lib/quant/quantErrors'

export const LIVE_LOG_POLL_MS = 3000

/**
 * Spec 041: tails a live session's log, with the semantics of `useQuantRunLog`: it reads from
 * byte offset 0 whenever it is enabled (so re-opening the panel replays the whole file),
 * appends each chunk, advances the offset, polls every 3 s only while the last answer said
 * `live`, re-reads at once while the server says `more` is waiting and keeps retrying with
 * back-off after a failure (text and offset kept). It stops when `live` is false, when
 * `enabled` turns false and on unmount. When `active` turns false it makes one final read.
 * Nothing is requested while the tab is hidden. Returns the same shape as the run log, so
 * the panel is shared.
 */
export function useQuantLiveLog(
  id: string,
  enabled: boolean,
  active = true,
): QuantRunLogState & { retry: () => void } {
  const [state, setState] = useState<QuantRunLogState>({
    text: '',
    live: true,
    loaded: false,
    error: null,
  })
  const [nonce, setNonce] = useState(0)
  const activeRef = useRef(active)
  const prevActive = useRef(active)
  const finalRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    activeRef.current = active
    if (prevActive.current && !active) finalRef.current?.()
    prevActive.current = active
  }, [active])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let controller: AbortController | null = null
    let inFlight = false
    let offset = 0
    let failures = 0
    let first = true
    let done = false
    let finalPending = false

    const isHidden = () => document.visibilityState === 'hidden'
    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    }
    const schedule = (ms: number) => {
      clearTimer()
      if (cancelled || done || isHidden()) return
      timer = setTimeout(poll, ms)
    }

    function poll() {
      timer = null
      if (cancelled || inFlight || isHidden()) return
      finalPending = false
      inFlight = true
      const own = new AbortController()
      controller = own
      quantApi.getLiveLog(id, offset, own.signal).then(
        (result) => {
          if (cancelled || own.signal.aborted) return
          inFlight = false
          failures = 0
          offset = result.offset
          const replace = first
          first = false
          setState((prev) => ({
            text: (replace ? '' : prev.text) + result.data,
            live: result.live,
            loaded: true,
            error: null,
          }))
          if (result.more) poll()
          else if (finalPending) poll()
          else if (result.live && activeRef.current) schedule(LIVE_LOG_POLL_MS)
          else done = true
        },
        (error: unknown) => {
          if (cancelled || own.signal.aborted) return
          inFlight = false
          failures += 1
          setState((prev) => ({ ...prev, error: quantErrorMessage(error) }))
          schedule(nextDelay(failures))
        },
      )
    }

    finalRef.current = () => {
      if (cancelled) return
      finalPending = true
      done = false
      clearTimer()
      if (!inFlight) poll()
    }

    function onVisibility() {
      if (cancelled) return
      if (isHidden()) {
        clearTimer()
        return
      }
      if ((!done || finalPending) && !inFlight) {
        clearTimer()
        poll()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    poll()
    return () => {
      cancelled = true
      clearTimer()
      controller?.abort()
      finalRef.current = null
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [id, enabled, nonce])

  const retry = useCallback(() => {
    setState({ text: '', live: true, loaded: false, error: null })
    setNonce((n) => n + 1)
  }, [])
  return { ...state, retry }
}
